import React, { useState, useEffect } from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import EncyclopediaView from './components/EncyclopediaView';
import ShuffleView from './components/ShuffleView';
import CustomizeView from './components/CustomizeView';
import AddMoveModal from './components/AddMoveModal';
import AddComboModal from './components/AddComboModal';
import AuthModal from './components/AuthModal';
import Footer from './components/Footer';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Toaster } from './components/ui/toaster';
import { Badge } from './components/ui/badge';
import { Book, Shuffle, Home, Sparkles, Settings, User, LogOut, Zap, Menu, X, Users, ListChecks } from 'lucide-react';
import { parseCSV } from './data/mockData';
import { Switch } from './components/ui/switch';
import TransitionMatrixView from './components/TransitionMatrixView';
import CrewsView from './components/CrewsView';
import RoutineBuilderView from './components/RoutineBuilderView';
import RevisionView from './components/RevisionView';
import ProfileView from './components/ProfileView';
import RadioPlayer from './components/RadioPlayer';

const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'light';
    }
    return 'dark';
  });

  useEffect(() => {
    if (theme === 'dark') {
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.body.classList.remove('dark');
      document.body.classList.add('light');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  return [theme, setTheme];
};

const Navigation = ({ activeView, setActiveView, theme, setTheme }) => {
  const { user, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'encyclopedia', label: 'Arsenal', icon: Book },
    { id: 'crews', label: 'Crews', icon: Users },
    { id: 'routines', label: 'Routines', icon: ListChecks },
    { id: 'shuffle', label: 'Mixer', icon: Shuffle },
    { id: 'transitions', label: 'Transitions', icon: Sparkles },
    { id: 'revision', label: 'Revision', icon: Zap },
    { id: 'customize', label: 'Studio', icon: Settings },
    { id: 'profile', label: 'Profile', icon: User }, // Profile à la fin
  ];

  return (
    <>
      <nav className={`shadow-2xl border-b-4 sticky top-0 z-50 ${theme === 'dark' ? 'bg-black border-white' : 'bg-white border-black'}`}>
        <div className="w-full px-4">
          <div className="flex items-center py-4 gap-4">
            {/* Left: Logo */}
            <div className="flex items-center space-x-3 min-w-[140px]">
              <h1 className={`text-3xl font-black tracking-wider ${theme === 'dark' ? 'text-white' : 'text-black'}`}>crewmate</h1>
              <Zap className={`w-6 h-6 ${theme === 'dark' ? 'text-white' : 'text-black'}`} />
            </div>
            {/* Center: Desktop nav */}
            {user && (
              <div className="hidden md:flex flex-1 justify-start flex-wrap gap-2">
                {navItems.map(item => {
                  const Icon = item.icon;
                  return (
                    <Button
                      key={item.id}
                      variant={activeView === item.id ? "default" : "ghost"}
                      onClick={() => setActiveView(item.id)}
                      className={`transition-all duration-200 font-bold tracking-wide border-2 px-2 py-1 ${
                        activeView === item.id 
                          ? 'bg-white text-black border-black hover:bg-gray-200' 
                          : `${theme === 'dark' ? 'text-white border-white hover:text-black hover:bg-white' : 'text-black border-black hover:bg-gray-100'}`
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      <span className="hidden lg:inline">{item.label}</span>
                    </Button>
                  );
                })}
              </div>
            )}
            {/* Right: Theme + user */}
            <div className="hidden md:flex items-center gap-2 ml-auto">
              <div className="flex items-center gap-2">
                <span className={`font-bold text-xs ${theme === 'dark' ? 'text-white' : 'text-black'}`}>{theme === 'dark' ? 'Dark' : 'Light'}</span>
                <Switch checked={theme === 'dark'} onCheckedChange={v => setTheme(v ? 'dark' : 'light')} />
              </div>
              {user ? (
                <>
                  <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 ${theme === 'dark' ? 'bg-white text-black border-white' : 'bg-white text-black border-black'}`}>
                    <User className="w-4 h-4" />
                    <span className="text-sm font-bold">{user.profile?.displayName || user.username}</span>
                    {user.profile?.crewName && (
                      <Badge className={`${theme === 'dark' ? 'bg-black text-white border border-white' : 'bg-black text-white border border-white'} font-bold text-xs`}>
                        {user.profile.crewName}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                    className={`${theme === 'dark' ? 'text-white border-white hover:text-black hover:bg-white' : 'text-black border-black hover:text-white hover:bg-black'} border-2 transition-all duration-200`}
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setShowAuthModal(true)}
                  className={`${theme === 'dark' ? 'bg-white text-black border-white' : 'bg-white text-black border-black'} font-black hover:bg-gray-200 transition-all duration-200 border-2`}
                >
                  <User className="w-4 h-4 mr-2" />
                  JOIN THE CREW
                </Button>
              )}
            </div>
            {/* Mobile hamburger */}
            <div className="md:hidden flex items-center">
              <button onClick={() => setMobileMenuOpen(true)} className="p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500">
                <Menu className={`w-8 h-8 ${theme === 'dark' ? 'text-white' : 'text-black'}`} />
              </button>
            </div>
          </div>
        </div>
        {/* Mobile menu overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-70 flex justify-end md:hidden">
            <div className={`w-64 h-full bg-white dark:bg-black shadow-lg flex flex-col p-6 space-y-6 relative`}>
              <button onClick={() => setMobileMenuOpen(false)} className="absolute top-4 right-4 p-2 rounded focus:outline-none">
                <X className="w-7 h-7 text-black dark:text-white" />
              </button>
              <div className="flex flex-col space-y-4 mt-10">
                {user && navItems.map(item => {
                  const Icon = item.icon;
                  return (
                    <Button
                      key={item.id}
                      variant={activeView === item.id ? "default" : "ghost"}
                      onClick={() => { setActiveView(item.id); setMobileMenuOpen(false); }}
                      className={`w-full justify-start transition-all duration-200 font-bold tracking-wide border-2 mb-1 ${
                        activeView === item.id 
                          ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white' 
                          : 'text-black border-black hover:text-white hover:bg-black dark:text-white dark:border-white dark:hover:text-black dark:hover:bg-white'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </Button>
                  );
                })}
              </div>
              <div className="flex flex-col space-y-3 mt-6">
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-xs text-black dark:text-white">{theme === 'dark' ? 'Dark' : 'Light'}</span>
                  <Switch checked={theme === 'dark'} onCheckedChange={v => setTheme(v ? 'dark' : 'light')} />
                </div>
                {user ? (
                  <div className="flex items-center space-x-2 px-3 py-2 bg-black text-white dark:bg-white dark:text-black rounded-lg border-2 border-black dark:border-white">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-bold">{user.profile?.displayName || user.username}</span>
                    {user.profile?.crewName && (
                      <Badge className="bg-white text-black font-bold text-xs border border-black dark:bg-black dark:text-white dark:border-white">
                        {user.profile.crewName}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <Button
                    onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                    className="bg-black text-white font-black hover:bg-gray-800 transition-all duration-200 border-2 border-black dark:bg-white dark:text-black dark:border-white"
                  >
                    <User className="w-4 h-4 mr-2" />
                    JOIN THE CREW
                  </Button>
                )}
                {user && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                    className="text-black dark:text-white hover:text-red-600 border-2 border-black dark:border-white transition-all duration-200"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </>
  );
};

const HomeView = ({ totalMoves, totalVariations }) => {
  const { user, userPresets } = useAuth();
  // Calcul du nombre de possibilités (3 moves, 2 variations)
  const moves = user?.moves || [];
  const variations = user?.customVariations ? Object.values(user.customVariations).flat() : [];
  const possibilities = moves.length > 0 && variations.length > 0
    ? Math.pow(moves.length, 3) * Math.pow(variations.length, 2)
    : 0;
  
  return (
    <div className="min-h-screen bg-black text-white space-y-8 p-8">
      {/* Hero Section */}
      <div className="text-center py-16 bg-gray-900 rounded-lg border-4 border-white">
        <h2 className="text-6xl font-black text-white mb-4 tracking-wider">
          {user ? `YO ${(user.profile?.displayName || user.username).toUpperCase()}!` : 'WELCOME TO CREWMATE'}
        </h2>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8 font-medium tracking-wide">
          {user 
            ? 'YOUR PERSONALIZED HIP HOP BREAKING COMPANION IS LOCKED AND LOADED. TIME TO DROP SOME FRESH COMBOS!'
            : 'THE ULTIMATE HIP HOP BREAKING COMPANION. JOIN THE CREW AND BUILD YOUR OWN LEGENDARY ARSENAL.'
          }
        </p>
        <div className="flex justify-center flex-wrap gap-4">
          {user ? (
            <>
              <div className="flex items-center bg-white text-black px-6 py-3 rounded-lg border-2 border-black">
                <Sparkles className="w-5 h-5 mr-2" />
                <span className="text-sm font-bold tracking-wide">
                  {userPresets[user.preset]?.name.toUpperCase() || 'CUSTOM SETUP'}
                </span>
              </div>
              {user.profile?.crewName && (
                <div className="flex items-center bg-black text-white px-6 py-3 rounded-lg border-2 border-white">
                  <span className="text-sm font-bold tracking-wide">CREW: {user.profile.crewName.toUpperCase()}</span>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center bg-white text-black px-6 py-3 rounded-lg border-2 border-black">
              <Settings className="w-5 h-5 mr-2" />
              <span className="text-sm font-bold tracking-wide">JOIN TO GET STARTED</span>
            </div>
          )}
        </div>
      </div>
      {user && possibilities > 0 && (
          <div className="flex justify-center mt-6">
            <span className="inline-block bg-white text-black font-black text-lg px-6 py-3 rounded-full border-4 border-black shadow-lg">
              With your arsenal, you can generate over {possibilities.toLocaleString()} unique combos!
            </span>
          </div>
        )}
        
      {/* Features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-8 bg-gray-900 border-4 border-white hover:bg-gray-800 transition-all duration-200">
          <div className="p-6">
          <div className="text-4xl mb-4">📚</div>
          <h3 className="text-2xl font-black text-white mb-3 tracking-wide">YOUR ARSENAL</h3>
          <p className="text-gray-300 font-medium leading-relaxed">
            Build your legendary collection with detailed breakdowns, 
            video tutorials, and custom descriptions that match your style.
          </p>
          </div>
        </Card>
        
        <Card className="p-8 bg-gray-900 border-4 border-white hover:bg-gray-800 transition-all duration-200">
          <div className="text-4xl mb-4">🎛️</div>
          <h3 className="text-2xl font-black text-white mb-3 tracking-wide">BEAT MIXER</h3>
          <p className="text-gray-300 font-medium leading-relaxed">
            Drop fresh combinations using your personalized 
            variations and custom move categories. Every session hits different.
          </p>
        </Card>

        <Card className="p-8 bg-gray-900 border-4 border-white hover:bg-gray-800 transition-all duration-200">
          <div className="p-6">
          <div className="text-4xl mb-4">🏭</div>
          <h3 className="text-2xl font-black text-white mb-3 tracking-wide">YOUR STUDIO</h3>
          <p className="text-gray-300 font-medium leading-relaxed">
            Design your own flavor categories, import moves from CSV, 
            and craft the perfect breaking experience that represents you.
          </p>
          </div>
        </Card>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-6 bg-white text-black p-8 rounded-lg border-4 border-black">
        <div className="text-center">
          <div className="text-4xl font-black">{user ? totalMoves : '???'}</div>
          <div className="font-bold tracking-wide">YOUR MOVES</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-black">5</div>
          <div className="font-bold tracking-wide">STYLE TYPES</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-black">{user ? totalVariations : '???'}</div>
          <div className="font-bold tracking-wide">YOUR FLAVORS</div>
        </div>
      </div>

      {/* Getting Started or User Journey */}
      {user ? (
        <Card className="p-8 bg-gray-900 border-4 border-white">
          <h3 className="text-2xl font-black mb-6 text-center text-white tracking-wide">YOUR CREWMATE JOURNEY</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div className="text-center border-2 border-white p-4">
              <div className="text-3xl mb-3">✅</div>
              <p className="font-bold text-white">CREW INITIATED</p>
              <p className="text-gray-300">with {userPresets[user.preset]?.name.toUpperCase() || 'CUSTOM'} setup</p>
              <p className="text-xs text-gray-400 mt-2">Active since {user.profile?.joinedDate}</p>
            </div>
            <div className="text-center border-2 border-white p-4">
              <div className="text-3xl mb-3">📈</div>
              <p className="font-bold text-white">BUILDING ARSENAL</p>
              <p className="text-gray-300">expanding your move collection and flavor bank</p>
              <p className="text-xs text-gray-400 mt-2">{totalMoves} moves, {totalVariations} flavors</p>
            </div>
            <div className="text-center border-2 border-white p-4">
              <div className="text-3xl mb-3">🎯</div>
              <p className="font-bold text-white">DROPPING COMBOS</p>
              <p className="text-gray-300">creating fresh combinations daily</p>
              <p className="text-xs text-gray-400 mt-2">Keep the creativity flowing!</p>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-8 bg-gray-900 border-4 border-white">
          <h3 className="text-2xl font-black mb-6 text-center text-white tracking-wide">JOIN THE CREW TODAY</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div className="text-center border-2 border-white p-4">
              <div className="text-3xl mb-3">1️⃣</div>
              <p className="font-bold text-white">PICK YOUR VIBE</p>
              <p className="text-gray-300">Choose a preset that matches your breaking level</p>
            </div>
            <div className="text-center border-2 border-white p-4">
              <div className="text-3xl mb-3">2️⃣</div>
              <p className="font-bold text-white">BUILD YOUR STYLE</p>
              <p className="text-gray-300">Customize variations and stack your move arsenal</p>
            </div>
            <div className="text-center border-2 border-white p-4">
              <div className="text-3xl mb-3">3️⃣</div>
              <p className="font-bold text-white">DROP THE HEAT</p>
              <p className="text-gray-300">Generate legendary combinations for practice</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

const AppContent = ({ theme, setTheme }) => {
  const { user, updateUserVariations, updateUserMoves, addCombo, updateCombo } = useAuth();
  const [activeView, setActiveView] = useState(() => {
    // Récupérer la vue active depuis localStorage au chargement initial
    if (typeof window !== 'undefined') {
      return localStorage.getItem('activeView') || 'home';
    }
    return 'home';
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddComboModal, setShowAddComboModal] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [editingMove, setEditingMove] = useState(null);

  // Persister la vue active dans localStorage à chaque changement
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('activeView', activeView);
    }
  }, [activeView]);

  const handleAddMove = async (newMove) => {
    if (user) {
      if (editingMove) {
        // Update existing move
        const updatedMoves = user.moves.map(move => 
          move.id === editingMove.id ? newMove : move
        );
        await updateUserMoves(updatedMoves);
        setEditingMove(null);
      } else {
        // Add new move
        const updatedMoves = [...user.moves, newMove];
        await updateUserMoves(updatedMoves);
      }
      
      // Si un dossier cible est spécifié, placer le move dans ce dossier
      if (newMove.targetFolder && newMove.targetSubfolder) {
        // Cette logique sera gérée par TreeViewEncyclopedia
        // Le move sera automatiquement placé dans le bon dossier lors du prochain rendu
      }
    }
  };

  const handleAddCombo = (newCombo) => {
    if (user) {
      if (editingCombo) {
        updateCombo(editingCombo.id, newCombo);
        setEditingCombo(null);
      } else {
        addCombo(newCombo);
      }
    }
  };

  const handleEditCombo = (combo) => {
    setEditingCombo(combo);
    setShowAddComboModal(true);
  };

  const handleEditMove = (move) => {
    setEditingMove(move);
    setShowAddModal(true);
  };

  const handleImportMoves = (movesArray) => {
    if (user) {
      const updatedMoves = [...user.moves, ...movesArray];
      updateUserMoves(updatedMoves);
    }
  };

  const handleUpdateVariations = (newVariations) => {
    if (user) {
      updateUserVariations(newVariations);
    }
  };

  const totalVariations = user ? Object.values(user.customVariations).reduce((acc, arr) => acc + arr.length, 0) : 0;
  const totalMoves = user ? user.moves.length : 0;

  const renderView = () => {
    switch (activeView) {
      case 'home':
        return <HomeView totalMoves={user?.moves?.length || 0} totalVariations={Object.values(user?.customVariations || {}).flat().length} />;
      case 'encyclopedia':
        return <EncyclopediaView onAddMove={() => setShowAddModal(true)} onAddCombo={() => setShowAddComboModal(true)} onEditCombo={handleEditCombo} onEditMove={handleEditMove} />;
      case 'crews':
        return <CrewsView />;
      case 'routines':
        return <RoutineBuilderView />;
      case 'shuffle':
        return <ShuffleView />;
      case 'customize':
        return (
          <CustomizeView 
            userVariations={user?.customVariations || {}} 
            onUpdateVariations={handleUpdateVariations}
            onImportMoves={handleImportMoves}
            userMoves={user?.moves || []}
          />
        );
      case 'transitions':
        return <TransitionMatrixView />;
      case 'revision':
        return <RevisionView />;
      case 'profile':
        return <ProfileView />;
      default:
        return <HomeView totalMoves={user?.moves?.length || 0} totalVariations={Object.values(user?.customVariations || {}).flat().length} />;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Navigation activeView={activeView} setActiveView={setActiveView} theme={theme} setTheme={setTheme} />
      
      <main>
        {renderView()}
      </main>

      {user && (
        <>
          <AddMoveModal
            isOpen={showAddModal}
            onClose={() => { setShowAddModal(false); setEditingMove(null); }}
            onAddMove={handleAddMove}
            editingMove={editingMove}
          />
          <AddComboModal
            isOpen={showAddComboModal}
            onClose={() => { setShowAddComboModal(false); setEditingCombo(null); }}
            onAddCombo={handleAddCombo}
            editingCombo={editingCombo}
          />
        </>
      )}

      <RadioPlayer />
      <Toaster />
      <Footer activeView={activeView} setActiveView={setActiveView} />
    </div>
  );
};

const App = () => {
  const [theme, setTheme] = useTheme();
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent theme={theme} setTheme={setTheme} />
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;